/**
 * Life4Earth V2 — Mini-Project detail page.
 *
 * Stage 3e header refactor (2026-05-19, Skip's redirect):
 * Mini-Project now renders through <PillarPageShell> with pillarType
 * "projects" — same chrome the Full Project uses (hero with side
 * bleed, max-width, shading, owner-avatar tray, community breadcrumb
 * path with country flag, frosted metadata card, social stats, sticky
 * tab bar). Cross-tier consistency by construction: any future polish
 * to the shared header propagates to both tiers.
 *
 * Hero upload control passes through `heroEditControls` and is
 * Lead-only. Gear/settings opens MiniProjectSettingsModal via
 * `onEdit`. Lifecycle transitions + visibility + archive live inside
 * the Settings modal (Skip's prior redirect).
 *
 * Spec: Mini_Project_Framework_Spec.docx §14.4-14.6.
 *
 * NOTE: Strings hardcoded pending Mini-Project i18n sweep (DEV-1062).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useApiMiniProject } from "../../api/useApiMiniProject";
import { useApiCommunityData } from "../../api/useApiCommunityData";
import { updateMiniProject } from "../../api/miniProjects";
import { fetchOwnerDisplay } from "../../api/ownerDisplay";
import { isUserSystemAdmin } from "../../security/securityAdapter";
import { localizedProjectStatus } from "../../Data/ProjectConfig/projectStandardOptions";
import { miniLifecycleToProjectStatus } from "../../Data/MiniProjectConfig/miniProjectLifecycle";
import PillarPageShell from "../../Components/PillarPage/PillarPageShell";
import FollowButton from "../../Components/FollowButton/FollowButton";
import { useHeroEdit } from "../../Components/PillarPage/useHeroEdit";
import type { PillarData, TabDefinition } from "../../lib/pillarTypes";
import ProgressTab from "./ProgressTab";
import AboutTabPanel from "./AboutTabPanel";
import MiniProjectCompletionDialog from "../../Components/MiniProjectCompletionDialog/MiniProjectCompletionDialog";
import SectionEditorModal from "../../Components/SectionEditorModal/SectionEditorModal";
import PlaybookCreationStepper from "../../Components/PlaybookCreationStepper/PlaybookCreationStepper";
import MasterPublishPanel from "../../Components/MasterPublishPanel/MasterPublishPanel";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import MediaTabPanel from "./MediaTabPanel";
import JournalTab from "../../Components/ProjectJournal/JournalTab";
import MiniProjectSettingsModal from "./MiniProjectSettingsModal";

// Task #225. "progress" renamed to "management" at the URL/tab-key
// level. The route /mini-projects/:id/progress still resolves — see
// the requestedTab mapping below for back-compat — so existing
// bookmarks and saved links don't break.
type TabKey = "management" | "about" | "media";

interface Props {
  currentUserGuid: string;
}

export default function MiniProjectDetailPage({ currentUserGuid }: Props) {
  const { t } = useAppTranslation("pages");
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { miniProject, loading, error, notFound, refetch } = useApiMiniProject(id);

  // ── Split-screen return state (2026-05-27, mirrors FP DEV-649) ──
  // When the user clicks "View Full" on a split-screen MP, the source
  // panel passes { returnTo, returnLabel, restoreProjectGuid } via
  // router state. We read it here so the shell can render a "← Return
  // to split view" button in the header that snaps the user back to
  // /portfolio with this MP still selected. When state is absent the
  // user got here some other way (direct URL, bookmark, etc.) and no
  // restore button is shown.
  const locState = location.state as
    | { returnTo?: string; returnLabel?: string; restoreProjectGuid?: string }
    | null;
  const returnTo = locState?.returnTo;
  const returnLabel = locState?.returnLabel;
  const restoreProjectGuid = locState?.restoreProjectGuid;

  const handleRestore = useCallback(() => {
    if (!returnTo) return;
    navigate(returnTo, {
      state: {
        restoreProjectGuid: restoreProjectGuid || id,
      },
    });
  }, [navigate, returnTo, restoreProjectGuid, id]);

  // Tab routing: pick the requested tab, fall back to About when
  // Management isn't visible to this user (per migration 165
  // per-user gate; canReadProgress is the back-end signal — name
  // retained on the wire pending a separate rename pass).
  // Task #225: legacy "progress" URL maps to the new "management" key
  // so saved bookmarks still resolve.
  const canReadProgress = miniProject?.canReadProgress ?? false;
  const requestedTab: TabKey =
    tab === "about" ? "about" :
    tab === "media" ? "media" :
    "management";

  // 2026-05-28 — Skip flagged: clicking "View Full" from the portfolio
  // split-screen was landing on About instead of Management, even when
  // the user could read progress. Root cause was a stale-useState
  // initializer: `miniProject` is fetched async via useApiMiniProject,
  // so on the very first render it's null and `canReadProgress` is
  // therefore false. The initializer captured "about" once and never
  // resynced. CLAUDE.md flags this exact async-hook-init pattern.
  //
  // Fix: lazy-initialize to the URL-requested tab (lossless), then
  // re-resolve via useEffect when canReadProgress lands. Mirrors how
  // ProjectDetailPage re-syncs activeTabIndex against visibleTabKeys
  // when async-loaded permissions arrive.
  const [activeTabKey, setActiveTabKey] = useState<TabKey>(requestedTab);

  useEffect(() => {
    // Wait for the async useApiMiniProject hook to actually resolve
    // before deciding whether to override the URL-requested tab.
    // Without this guard we'd briefly snap to "about" (because
    // canReadProgress defaults to false while loading) and then back
    // to "management" once the data arrives — a visible flicker.
    if (!miniProject) return;
    const resolved: TabKey =
      requestedTab === "management" && !canReadProgress ? "about" : requestedTab;
    setActiveTabKey(resolved);
    // User-driven tab clicks call setActiveTabKey directly without
    // changing the URL, so this effect's deps don't fire on those —
    // preserves the user's explicit pick after first load.
  }, [miniProject, requestedTab, canReadProgress]);

  // Settings modal state — Lead-only access (with sysadmin bypass below).
  const [settingsOpen, setSettingsOpen] = useState(false);
  // U1b completion dialog + retrospective editor modal (DEV-1265).
  const [completionOpen, setCompletionOpen] = useState(false);
  const [retroOpen, setRetroOpen] = useState(false);
  // U2 playbook creation stepper (DEV-1266).
  const [playbookOpen, setPlaybookOpen] = useState(false);
  // U1b post-completion strip dismissal (decision #15) — session-level.
  const [stripDismissed, setStripDismissed] = useState(false);

  // "isLead" here is the canManageMiniProject gate the rest of this file and
  // its sub-tabs consume. Strict Lead match by OwnerUserGuid, OR sysadmin —
  // mirrors the backend's MiniProjectAuth.CanWriteMiniProject (owner OR
  // sysadmin) and the CLAUDE.md "isUserSystemAdmin fast-path" rule. Without
  // the sysadmin OR, a sysadmin loses access to the gear, Settings modal,
  // hero edit, About edit, Progress edit, and Media upload on any project
  // they don't personally Lead — bad for support, ops, and recovery flows.
  const strictlyLead = miniProject?.ownerUserGuid?.toUpperCase() === currentUserGuid?.toUpperCase();
  const isSysadmin = isUserSystemAdmin(currentUserGuid as any);
  const isLead = !!(strictlyLead || isSysadmin);

  /* ── Owner display resolution (avatar + name + click target) ──
     The "Owner" of a Mini-Project — per the Settings modal's Owner
     field and Skip's 2026-05-19 design — is the owning COMMUNITY
     (ownerCommunityGuid), not the Lead user. The Lead is shown
     separately in the modal's Project Info section.

     Resolution flow:
       fetchOwnerDisplay(ownerCommunityGuid, entities, relationships)
         → finds the community in the entities snapshot (or falls back
           to a per-GUID server lookup for private communities)
         → returns its effective logo via the inheritance cascade
         → returns kind='community' + displayName + avatarUrl

     PillarPageHeader then routes the avatar click to
     /communities/{guid}#alignment (vs /profile/{guid} for user owners).
     Same helper handles both cases so a future community→user owner
     swap requires no code change here — just pass the new GUID.

     Re-resolves whenever miniProject.ownerCommunityGuid changes (owner
     transfer via the Settings modal) AND when the community-entities
     snapshot loads in (async hook). The retry-once key prevents
     duplicate fetches during the empty→loaded transition. */
  const {
    communityEntities: ownerCheckCommunityEntities,
    communityRelationships: ownerCheckRelationships,
  } = useApiCommunityData();
  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<
    { kind: "user" | "community" | "unknown"; guid: string; displayName: string } | null
  >(null);
  const ownerResolvedForKey = useRef<string>("");

  useEffect(() => {
    if (!miniProject?.ownerCommunityGuid) return;
    let cancelled = false;
    const tg = miniProject.ownerCommunityGuid;
    const entitiesLoaded = (ownerCheckCommunityEntities ?? []).length > 0;
    const key = `${tg}|${entitiesLoaded ? "loaded" : "empty"}`;
    if (ownerResolvedForKey.current === key) return;
    ownerResolvedForKey.current = key;

    // Clear stale resolution from the previous owner before the new
    // lookup completes — prevents the previous owner's avatar flashing
    // briefly after an owner transfer.
    setOwnerAvatarUrl(null);
    setOwnerInfo(null);

    fetchOwnerDisplay(tg, ownerCheckCommunityEntities, ownerCheckRelationships)
      .then((display) => {
        if (cancelled) return;
        if (display.avatarUrl) setOwnerAvatarUrl(display.avatarUrl);
        setOwnerInfo({ kind: display.kind, guid: tg, displayName: display.displayName });
      })
      .catch(() => { /* fallback to initial letter handled by PillarPageHeader */ });

    return () => { cancelled = true; };
  }, [miniProject?.ownerCommunityGuid, ownerCheckCommunityEntities, ownerCheckRelationships]);

  // Hero edit (Path Y, shared hook) — persists via the Mini-Project
  // update endpoint. The hook owns crop, upload, dropdown, URL paste,
  // and remove; we just write the resulting URL back through the API.
  const persistMiniHero = useCallback(
    (url: string) => {
      if (!miniProject) return;
      updateMiniProject(miniProject.miniProjectGuid, { heroImageUrl: url })
        .then(() => refetch())
        .catch((err) => {
          window.alert("Hero update failed: " + (err instanceof Error ? err.message : String(err)));
        });
    },
    [miniProject, refetch],
  );
  const removeMiniHero = useCallback(() => {
    if (!miniProject) return;
    updateMiniProject(miniProject.miniProjectGuid, { heroImageUrl: "" })
      .then(() => refetch())
      .catch((err) => {
        window.alert("Hero remove failed: " + (err instanceof Error ? err.message : String(err)));
      });
  }, [miniProject, refetch]);

  // Resolve the primary location's display name from the community-
  // entities snapshot. PillarPageHeader's breadcrumb builder uses
  // communityL3 || L2 || L1 as the LEAF segment name; without one,
  // it silently drops the leaf and only renders ancestors (Skip
  // reported "first subcommunity is cut off"). Falls back to owner
  // community name when no explicit primary location is set.
  const primaryLocationName = useMemo(() => {
    if (!miniProject) return "";
    const guid = (miniProject.locationGeographyGuid || miniProject.ownerCommunityGuid || "").toUpperCase();
    if (!guid) return "";
    const entities = Array.isArray(ownerCheckCommunityEntities) ? ownerCheckCommunityEntities : [];
    const found = entities.find(
      (e: any) => String(e?.entity_guid || "").toUpperCase() === guid,
    );
    return String(found?.name || "");
  }, [miniProject, ownerCheckCommunityEntities]);

  // ── PillarData (shape the shared header consumes) ──
  // Built unconditionally with safe defaults so the useMemo dep list
  // stays simple. Returns null when miniProject isn't loaded yet; the
  // guard below short-circuits rendering.
  const pillarData: PillarData | null = useMemo(() => {
    if (!miniProject) return null;
    return {
      guid: miniProject.miniProjectGuid,
      name: miniProject.title,
      description: miniProject.shortDescription || undefined,
      category: miniProject.categoryKey || "",
      // DEV-1271 (Skip 2026-06-08): the header's main status is now the
      // LIFECYCLE status (Planned / In Progress / Completed / Expired),
      // localized via the canonical project_status path — replacing the
      // old soft `projectStatus` label. The soft field still exists (the
      // Settings dropdown) but is no longer the displayed status.
      status: localizedProjectStatus(
        miniLifecycleToProjectStatus(miniProject.status) || miniProject.status || "",
      ),
      // Verification status (Skip's status #2) — shown as the "Content"
      // chip in the header tray.
      contentStatus: miniProject.contentStatus || "",
      // DEV-1271: the separate "Lifecycle" tray chip (DEV-1112) is now
      // redundant — the main status above IS the lifecycle. Omit it so the
      // header doesn't show the lifecycle twice. (Passing undefined hides
      // the chip via the `data.lifecycleStatus &&` gate in PillarPageHeader.)
      lifecycleStatus: undefined,
      startDate: miniProject.startDate || undefined,
      endDate: miniProject.endDate || undefined,
      heroImageUrl: miniProject.heroImageUrl || undefined,
      // Mini-Project doesn't track these yet — pass safe defaults so the
      // shared social-stats row in PillarPageHeader still renders cleanly.
      followersCount: 0,
      contributorsCount: 0,
      sdgBadges: [],
      // Community path resolution. PillarPageHeader walks ancestors from
      // primaryLocationGuid AND uses communityL3/L2/L1 for the LEAF
      // segment name. Without a leaf name, only ancestors render (the
      // bug Skip reported as "first subcommunity is cut off").
      primaryLocationGuid:
        miniProject.locationGeographyGuid ||
        miniProject.ownerCommunityGuid ||
        undefined,
      communityL3: primaryLocationName || undefined,
      // Owner tray — Mini-Project's owner is the owning COMMUNITY
      // (ownerCommunityGuid). Avatar / name resolved asynchronously
      // via fetchOwnerDisplay (above) → kind='community' for the
      // community case. PillarPageHeader uses ownerKind + ownerGuid
      // to wire the click target: community → /communities/{guid}#alignment.
      ownerKind: ownerInfo?.kind === "unknown" ? undefined : ownerInfo?.kind,
      ownerGuid: ownerInfo?.guid || miniProject.ownerCommunityGuid,
      ownerDisplayName: ownerInfo?.displayName || undefined,
      ownerAvatarUrl: ownerAvatarUrl || undefined,
      relationships: {
        problems_count: 0,
        solutions_count: 0,
        enablers_count: 0,
        communities_count: 0,
        projects_count: 0,
      },
    };
  }, [miniProject, ownerAvatarUrl, ownerInfo, primaryLocationName]);

  // ── Hero edit (Path Y, shared hook) ──
  // Returns the same three nodes Full Project plugs into PillarPageShell:
  // floating Edit-cover-photo menu, full-bleed crop overlay, and the menuOpen
  // flag so the shell can let the dropdown overflow the header.
  const {
    menu: heroEditControls,
    overlay: heroCropOverlay,
    menuOpen: heroMenuOpen,
  } = useHeroEdit({
    canEdit: !!isLead,
    currentHeroUrl: miniProject?.heroImageUrl ?? null,
    uploadBucket: "mini-project-hero",
    uploadEntityKey: miniProject?.miniProjectGuid,
    onPersist: persistMiniHero,
    onRemove: removeMiniHero,
  });

  // ── Tabs ──
  const tabs: TabDefinition[] = useMemo(() => {
    if (!miniProject) return [];
    const out: TabDefinition[] = [];
    if (canReadProgress) {
      out.push({
        key: "management",
        label: "Management",
        content: (
          <ProgressTab
            miniProject={miniProject}
            isLead={isLead}
            currentUserGuid={currentUserGuid}
            onMutated={refetch}
          />
        ),
      });
    }
    out.push({
      key: "about",
      label: "About",
      content: (
        <AboutTabPanel
          miniProject={miniProject}
          isLead={isLead}
          currentUserGuid={currentUserGuid}
          onMutated={refetch}
        />
      ),
    });
    out.push({
      key: "journal",
      label: "Journal",
      content: (
        <JournalTab
          entityType="mini_project"
          entityGuid={miniProject.miniProjectGuid}
          canShare={isLead}
        />
      ),
    });
    out.push({
      key: "media",
      label: "Media",
      content: (
        <MediaTabPanel
          miniProjectGuid={miniProject.miniProjectGuid}
          isLead={isLead}
          currentUserGuid={currentUserGuid}
        />
      ),
    });
    return out;
  }, [miniProject, canReadProgress, isLead, currentUserGuid, refetch]);

  if (loading) {
    return <div style={statusMsgStyle}>Loading…</div>;
  }
  if (error) {
    return <div style={statusMsgStyle}>Failed to load: {error}</div>;
  }
  if (notFound || !miniProject || !pillarData) {
    return <div style={statusMsgStyle}>Mini-Project not found.</div>;
  }

  const activeTabIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === activeTabKey),
  );

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      {/* U2 (DEV-1266): master-copy banner. A playbook's master looks like
          a project but never runs — it's the editable blueprint. Full
          author-mode (Publish, version history) arrives in U3. */}
      {miniProject.isMaster && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 16px",
            background: "color-mix(in srgb, var(--color-success) 12%, transparent)",
            borderBlockEnd: "1px solid color-mix(in srgb, var(--color-success) 30%, transparent)",
            color: "var(--text-primary)", fontSize: 13,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 18 }}>🌱</span>
          <span>
            <strong>{t("miniProject.master.bannerTitle")}</strong>{" — "}
            {t("miniProject.master.bannerHint")}
          </span>
        </div>
      )}
      {/* U3 (DEV-1267): author publish controls + version history. */}
      {miniProject.isMaster && isLead && (
        <MasterPublishPanel masterMiniProjectGuid={miniProject.miniProjectGuid} />
      )}
      {/* U1b (decision #15): post-completion re-entry strip. Lead-only, on
          completed non-master projects. The wrap-up actions (retrospective,
          playbook) live here so they're reachable after the completion
          dialog is dismissed. ✕ dismisses for the session. */}
      {isLead && !miniProject.isMaster && miniProject.status === "complete" && !stripDismissed && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            padding: "9px 16px",
            background: "color-mix(in srgb, var(--color-success) 9%, transparent)",
            borderBlockEnd: "1px solid var(--border-subtle)",
            fontSize: 13,
          }}
        >
          <span aria-hidden="true">🏁</span>
          <span style={{ color: "var(--text-secondary)" }}>{t("miniProject.completion.stripPrompt")}</span>
          <button
            type="button"
            onClick={() => setRetroOpen(true)}
            style={completionStripBtnStyle}
          >
            📝 {t("miniProject.completion.retroTitle")}
          </button>
          <button
            type="button"
            onClick={() => setPlaybookOpen(true)}
            style={{ ...completionStripBtnStyle, color: "var(--color-success)" }}
          >
            🌱 {t("miniProject.completion.playbookTitle")}
          </button>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => setStripDismissed(true)}
            aria-label={t("miniProject.completion.close")}
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 15 }}
          >
            ✕
          </button>
        </div>
      )}
      <PillarPageShell
        pillarType="projects"
        data={pillarData}
        tabs={tabs}
        activeTabIndex={activeTabIndex}
        onTabChange={(idx) => setActiveTabKey(tabs[idx].key as TabKey)}
        heroImageUrl={miniProject.heroImageUrl || undefined}
        heroEditControls={heroEditControls}
        headerActions={<FollowButton targetType="mini_project" targetGuid={miniProject.miniProjectGuid} compact showCount={false} />}
        heroCropOverlay={heroCropOverlay}
        heroMenuOpen={heroMenuOpen}
        isHeroExpanded
        // Gear/Settings in the shell's top-right zone → opens our modal.
        onEdit={isLead ? () => setSettingsOpen(true) : undefined}
        // "← Return to split view" — only when we arrived via View Full
        // from the portfolio (router state carries returnTo). Mirrors
        // ProjectDetailPage's pattern. returnLabel is intentionally not
        // surfaced as a button label today because PillarPageShell uses
        // a fixed restore-icon affordance — preserved in the state
        // payload for future label-aware variants.
        onRestore={returnTo ? handleRestore : undefined}
      />

      {/* Settings modal — portal-rendered, Lead-only. */}
      {isLead && (
        <MiniProjectSettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          miniProject={miniProject}
          isLead={isLead}
          currentUserGuid={currentUserGuid}
          onSaved={refetch}
          // U1b: "Mark Completed" routes to the rich completion dialog
          // instead of a bare confirm. Close settings, open the dialog.
          onRequestComplete={() => {
            setSettingsOpen(false);
            setCompletionOpen(true);
          }}
        />
      )}

      {/* U1b completion dialog (DEV-1265) — confirm → celebrate + next steps. */}
      {isLead && completionOpen && (
        <MiniProjectCompletionDialog
          miniProject={miniProject}
          canCreatePlaybook={isLead}
          onClose={() => setCompletionOpen(false)}
          onCompleted={refetch}
          onWriteRetrospective={() => {
            setCompletionOpen(false);
            setRetroOpen(true);
          }}
          onCreatePlaybook={() => {
            setCompletionOpen(false);
            setPlaybookOpen(true);
          }}
        />
      )}

      {/* U2 playbook creation stepper. On success, navigate into the
          editable master (decision #14). */}
      {isLead && playbookOpen && (
        <PlaybookCreationStepper
          source={miniProject}
          onClose={() => setPlaybookOpen(false)}
          onCreated={(playbook) => {
            setPlaybookOpen(false);
            if (playbook.masterMiniProjectGuid) {
              navigate(`/projects/${playbook.masterMiniProjectGuid}/about`);
            }
          }}
        />
      )}

      {/* U1b retrospective editor — write it right here, no hunting.
          Uses the shared SectionEditorModal in find-or-create mode. */}
      {isLead && retroOpen && (
        <SectionEditorModal
          entityType="mini_project"
          entityGuid={miniProject.miniProjectGuid}
          findOrCreateType="retrospective"
          onClose={() => setRetroOpen(false)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}

const statusMsgStyle: React.CSSProperties = {
  padding: 48,
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: 14,
};

// U1b post-completion strip action buttons.
const completionStripBtnStyle: React.CSSProperties = {
  background: "var(--bg-hover)",
  border: "1px solid var(--border-default)",
  borderRadius: 7,
  padding: "4px 11px",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--info-text)",
  cursor: "pointer",
};
